import { cloneDeep, debounce } from 'lodash';
import store from '@/store';
import log, { logV2 } from '@/api/client/log';
import { encodeBase64, sessionRead, createSessionKey, localRead, localSave, isShellEmbedded } from '../util';
import { LogCache } from '@/libs/cache';
import { timeDayjs } from '@/libs/date';
import { getPlatForm, PlatFormType } from '@/api/sys/tools';
import { utils } from '@salesmartly/tools';

createSessionKey();

const tabKey = sessionRead('SESSION_TAB_KEY');

const LOG_BATCH_CACHE = `LOG_BATCH_CACHE_${tabKey}`;

const LOG_BATCH_CACHE_TABS = 'LOG_BATCH_TABS';

interface ItemCache {
    log_type: string;
    type: string;
    log_data: any;
}

interface ItemCacheLog {
    log_type: string;
    data: {
        type: string;
        log_data: any;
        c_t: PlatFormType;
        log_timestamp: string;
        source_url: string;
        url: string;
        ua: string;
        language: string;
        version: any;
        projectName: any;
        projectId: any;
        user_id: any;
    };
}

interface SaveData {
    data: string;
    _retryCount: number;
    updateTime: number;
}

const getGlobalParams = () => {
    // 嵌入模式追加 embedded/1 段,便于 Doris 用 ua LIKE '%embedded/1%' 过滤区分
    // iframe 与独立窗口的日志来源
    const uaSegments = [
        window.navigator.userAgent,
        tabKey ? `tabId/${tabKey}` : '',
        isShellEmbedded() ? 'embedded/1' : '',
    ].filter(Boolean);
    return {
        ua: uaSegments.join(' '),
        language: window.navigator.language,
        version: (window as any).version,
        projectName: store.state.user.project,
        projectId: store.state.user.projectId,
        user_id: store.state.user.userId,
        source_url: window.location.href,
        url: window.location.href,
    };
};

// 无需进入队列的白名单-type
const typeWhitelist: string[] = ['RECEIVE_NOTICE_LIST', 'WS_ERROR_RECEIVE_NOTICE_LIST'];

// 无需进入队列的白名单-log_type
const logTypeWhitelist: string[] = ['LOCAL_FORAGE_ERROR'];

/**
 * 上报日志
 * @param logType
 * @param type
 * @param log_data
 */
const logClient = (logType: string, type: string, log_data: any = {}) => {
    const commonParams = getGlobalParams();

    const params = {
        type,
        ...commonParams,
        log_data,
    };

    let logData: any = null;

    try {
        logData = JSON.stringify(params);
    } catch (e) {
        logData = JSON.stringify({
            type,
            data: e,
            log_data: e,
            ...commonParams,
        });
    }

    // console.log(['v1日志提交', logType, JSON.parse(logData)]);

    log({
        log_type: logType,
        data: encodeBase64(logData),
        base_encode: '1',
    }).catch(console.error);
};

class LogClient {
    constructor() {
        this.init();
    }

    // 日志队列
    private log_queue: SaveData[] = [];

    // 最大堆积条数
    private max_count = 10;

    // 最大堆积时间
    private max_time = 10 * 1000;

    // 定时器。定时提交日志
    private logTimer: NodeJS.Timeout | unknown = 0;

    // 延迟启动日志队列的时间 10分钟
    private delay_time = 10 * 60 * 1000;
    // private delay_time = 1 * 60 * 1000;
    // private delay_time = 5 * 1000;

    // 初始化时间
    private init_time = Date.now();

    private logger(...arg: any) {
        // console.log(...arg);
    }

    private localSetItem(key: string, val: any) {
        let local_data = val;
        try {
            local_data = JSON.stringify(val);
        } catch (error) {
            local_data = val;
        }
        localSave(key, local_data);
    }

    private localGetItem(key: string, val?: any) {
        const local_data = localRead(key) || '';
        if (!local_data) {
            return val;
        }
        try {
            return JSON.parse(local_data);
        } catch (error) {
            return local_data || val;
        }
    }

    async init() {
        const res = await LogCache.getItem(LOG_BATCH_CACHE, []);
        this.logger('---获取缓存的所有日志成功---', res);
        if (res && Array.isArray(res) && res.length) {
            this.postLog(res);
        }

        // 处理不同tab的集合
        let tabs: any = this.localGetItem(LOG_BATCH_CACHE_TABS, [LOG_BATCH_CACHE]);
        this.logger('---获取缓存的所有tab---', tabs);
        if (tabs && tabs.length) {
            if (!tabs.includes(LOG_BATCH_CACHE)) {
                tabs.push(LOG_BATCH_CACHE);
            }
            this.localSetItem(LOG_BATCH_CACHE_TABS, tabs);
        } else {
            tabs = [LOG_BATCH_CACHE];
            this.localSetItem(LOG_BATCH_CACHE_TABS, tabs);
        }

        LogCache.db.keys().then(async (keyList: string[]) => {
            if (keyList && keyList.length) {
                let result: SaveData[] = [];
                let count = 0;
                const new_tabs = this.localGetItem(LOG_BATCH_CACHE_TABS, [LOG_BATCH_CACHE]);
                const filterList = keyList.filter((key: string) => key.indexOf('LOG_BATCH_CACHE') > -1 && !new_tabs.includes(key));
                this.logger('---获取缓存keyList---', keyList, new_tabs);
                this.logger('----filterList----', filterList);
                filterList.forEach(async (key: string) => {
                    const resList: SaveData[] = await LogCache.getItem(key, []);
                    this.logger('---LogCache日志成功---', resList);
                    if (resList && Array.isArray(resList) && resList.length) {
                        result = [...resList, ...result];
                    }
                    LogCache.removeItem(key);
                    count += 1;
                    this.logger('----result----', count, filterList.length, result);
                    if (count >= filterList.length && result.length) {
                        this.postLog(result);
                    }
                });
            }
        });

        // 关闭或刷新页面前移除
        window.addEventListener('beforeunload', async () => {
            const new_tabs = this.localGetItem(LOG_BATCH_CACHE_TABS, []);
            const ls = new_tabs.filter((item: string) => item !== LOG_BATCH_CACHE);
            this.logger('---关闭或刷新页面前移除缓存的所有日志---', ls);
            this.localSetItem(LOG_BATCH_CACHE_TABS, ls);
        });
    }

    // 队列是否处于开启状态
    getStatus() {
        return Date.now() - this.init_time > this.delay_time;
    }

    // 定时器，定时提交日志
    setLogTimer() {
        clearTimeout(this.logTimer as NodeJS.Timeout);
        this.logTimer = setTimeout(() => {
            this.logger(['倒计时10s 提交日志', [...this.log_queue]]);
            this.postLog();
            this.logTimer = null;
        }, this.max_time);
    }

    // 存储日志数据
    debounceSaveLog = debounce(() => {
        LogCache.setItem(LOG_BATCH_CACHE, this.log_queue);
    }, 1000);

    // 清空日志表
    cleanCache() {
        this.log_queue.splice(0, this.log_queue.length);
        this.logger(['清空日志队列', this.log_queue]);
        this.debounceSaveLog();
    }

    // 提交日志
    postLog(list?: SaveData[]) {
        const log_list = cloneDeep(list || this.log_queue || []);
        if (log_list && log_list.length) {
            const data = log_list.map((item) => {
                let real_data: any = item.data;
                if (typeof item.data === 'string') {
                    try {
                        real_data = JSON.parse(utils.decodeBase64(item.data));
                        // this.logger(['解密后的日志', real_data, item.data]);
                    } catch (_) {
                        //
                    }
                }
                return real_data;
            });
            this.cleanCache();
            logV2({ data }).catch(() => {
                // 上传失败，将日志重新推入队列
                log_list.forEach((logTask: SaveData) => {
                    if (logTask._retryCount < 1) {
                        // 重试过一次的日志不继续进入队列
                        logTask._retryCount += 1;
                        this.log_queue.push(logTask);
                        if (this.log_queue.length >= this.max_count) {
                            this.postLog();
                        }
                    }
                });
                this.debounceSaveLog();
            });
        }
    }

    // 添加日志
    addLog(data: ItemCache) {
        // 不满足白名单内日志type并且开启日志队列时，将日志推进队列
        if (!typeWhitelist.includes(data.type) && !logTypeWhitelist.includes(data.log_type) && this.getStatus()) {
            if (!this.logTimer) {
                // 开启定时器
                this.logger(['开启日志批量上传定时器']);
                this.setLogTimer();
            }
            const params = {
                updateTime: Date.now(),
                _retryCount: 0,
                data: encodeBase64(JSON.stringify({
                    log_type: data.log_type,
                    data: {
                        ...getGlobalParams(),
                        type: data.type,
                        c_t: getPlatForm(),
                        log_data: data.log_data,
                        log_timestamp: timeDayjs().tz('Asia/Shanghai').format(),
                    },
                })),
            };
            this.log_queue.push(params);
            this.debounceSaveLog();
            if (this.log_queue.length >= this.max_count) {
                this.logger(['堆积满10条时，提交日志，重新开启定时器']);
                // 堆积满10条时，提交日志
                this.postLog();
                // 清空定时器
                clearTimeout(this.logTimer as NodeJS.Timeout);
                this.logTimer = null;
            }
            this.logger('----当前日志队列----', [...this.log_queue]);
        } else {
            // 直接采用旧接口上传
            logClient(data.log_type, data.type, data.log_data);
        }
    }
}

export default new LogClient();
